import { useGetFareFamilyInfoQuery } from '@/src/api/rtkQuery/fareFamilyApi'
import { useGetFareFamilyInfoV3Query } from '@/src/api/rtkQuery/fareFamilyApiV3'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'

/**
 * Base options that can be passed to the fare family query
 */
interface BaseFareFamilyOptions {
    skip?: boolean
    pollingInterval?: number
    refetchOnReconnect?: boolean
    refetchOnFocus?: boolean
    refetchOnMountOrArgChange?: boolean | number
}

/**
 * Options with selectFromResult for custom data selection
 */
interface FareFamilyOptionsWithSelect<Selected> extends BaseFareFamilyOptions {
    selectFromResult: (result: any) => Selected
}

/**
 * Hook overload: No options or options without selectFromResult
 * Returns the full query result object
 */
function useFareFamilyInfoApiCall(options?: BaseFareFamilyOptions): ReturnType<typeof useGetFareFamilyInfoQuery>

/**
 * Hook overload: With selectFromResult option
 * Returns the selected data type
 */
function useFareFamilyInfoApiCall<Selected>(
    options: FareFamilyOptionsWithSelect<Selected>
): Selected

/**
 * Implementation of the hook that conditionally calls V2 or V3 Fare Family API
 * 
 * @example Basic usage
 * ```ts
 * const { data, isLoading, isError } = useFareFamilyInfoApiCall()
 * ```
 * 
 * @example With skip
 * ```ts
 * const result = useFareFamilyInfoApiCall({ skip: !enabled })
 * ```
 * 
 * @example With selectFromResult - properly typed
 * ```ts
 * const { isLoading, isSuccess } = useFareFamilyInfoApiCall({
 *     selectFromResult: ({ isLoading, isSuccess }) => ({ isLoading, isSuccess })
 * })
 * ```
 */
function useFareFamilyInfoApiCall<Selected = never>(
    options?: BaseFareFamilyOptions | FareFamilyOptionsWithSelect<Selected>
) {
    const itineraryId = useGetItineraryId()
    
    // Determine trip type from itinerary data
    const { data: itineraryData } = useGetItineraryDataQuery({ itineraryId })
    const isRoundTrip = !!itineraryData?.searchCriteria?.arrivalDate
    
    // Extract skip from options
    const skip = options?.skip || false
    
    // Build query options - spread all options
    const queryOptions = options ? { ...options } : {}
    
    /**
     * Type assertion is needed here because:
     * 1. We're abstracting over two different APIs (V2 and V3) that return different types
     * 2. The function overloads ensure type safety for consumers
     * 3. The runtime behavior is correct - only one API is called at a time
     * 4. This is acceptable in a wrapper/abstraction layer
     */
    
    // Conditionally call the right API with all options passed through
    const v2Result = useGetFareFamilyInfoQuery(
        { itineraryId },
        { 
            ...queryOptions,
            skip: skip || isRoundTrip 
        } as any
    )
    
    const v3Result = useGetFareFamilyInfoV3Query(
        { itineraryId },
        { 
            ...queryOptions,
            skip: skip || !isRoundTrip 
        } as any
    )
    
    return isRoundTrip ? v3Result : v2Result
}

export default useFareFamilyInfoApiCall
